<!-- EnvironmentCheckTestRunner.vue -->
<!-- Notes: If the document in which the test runner isn't visisble, it can impact results due to the browser throttling methods in the invisible page -->
<script setup lang="ts">
    import { reactive, nextTick, onMounted, ref } from 'vue';
    import { timeout } from '../../lib/ChoiceUtils'
    import * as ChoiceApi from '../../lib/ChoiceApi'
    import { EnvironmentCheckResultsModel, EnvironmentCheckSettingsModel } from '../../lib/Api/CustomerSupport';
    import VisibilityMonitor from '../../components/Events/VisibilityMonitor.vue'
    import _, { delay } from 'lodash';

    const props = defineProps<{
        runAfterLaunchTimeMilliSec?: number
    }>();

    const compData = reactive<{
        runningTests?: boolean,
        restartTests?:boolean,
        runningfibonacciTest?: boolean,
        runningdomTest?: boolean,
        runningRemoteDownloadTest?: boolean,

        downloadResourceBytes?: number,
        fibonacciTestStatus?: string,

        results: EnvironmentCheckResultsModel
        settings: EnvironmentCheckSettingsModel
    }>({
        results: {},
        settings: {
            fibonacciTestLoopCount: 100,
			thirdPartyDownloadUrl: "https://isalus-environment-check-downloads.s3.us-east-2.amazonaws.com/100KB_test_file.txt",   // Was https://eu.httpbin.org/stream-bytes
            downloadResourceByteCount: 100*1024,
            downloadResourceReps: 5
        }
    });

    const runExecuting = ref(false);

    defineExpose({run, runExecuting });

    onMounted(() =>{
        if (undefined !== props.runAfterLaunchTimeMilliSec)
        {
            timeout(props.runAfterLaunchTimeMilliSec).then(() => {
                run();
            });
        }
    });

    async function run()
    {
        runExecuting.value = true;

        compData.runningTests = true;
        await runTests();
        compData.runningTests = false;

        // Save the sanity check data
        await ChoiceApi.customerSupport.saveEnvironmentCheckResult({
            results: compData.results,
            settings: compData.settings
        });
        runExecuting.value = false;            
    }

    function fibonacci(num: number): number[] {
        let a: number = 1, b: number = 0, temp: number;
        const result: number[] = [];

        while (num >= 0){
            temp = a;
            a = a + b;
            result.push(a);
            b = temp;
            num--;
        }
        return result;
    }

    async function fibonacciTest(num: number): Promise<number> {

        compData.runningfibonacciTest = true;
        await timeout(0);
        const start = new Date();

        const fibValues = fibonacci(num);

        // Generate string output
        const loopCount = compData.settings.fibonacciTestLoopCount;
        for(let i=0; i < loopCount; ++i)
        {	  
            const stringVal = `${Date.now()}: ${Math.max(...fibValues.map(item => 
                { 
                    return Math.log(Math.sqrt(item + (loopCount ?? 1) * Math.random()));
                }))}`;

            compData.fibonacciTestStatus = `Step: ${i+1} of ${loopCount}. ` + stringVal;
            await timeout(0);
        }

        const end = new Date();
        compData.runningfibonacciTest = false;

        return end.valueOf() - start.valueOf();
    }

    async function domTest(): Promise<number|undefined>
    {
        await timeout(0);
        const start = new Date();
        compData.runningdomTest = true;

        const ele  = document.querySelector("#domTestArea") as (HTMLElement|null|undefined);
        if (!ele)
        {
            alert("Problem getting element");
            return;
        }

        ele.innerHTML = "";
        ele.style.display = "block";

        const fibValues = fibonacci(250);
        for(const val of fibValues)
        {
            const computedValue = Math.tan(Math.log10(Math.sqrt(val)));
            const computedValue2 = Math.fround(Math.sqrt(val));

            if (ele.innerHTML.length)
                ele.innerHTML += ", ";
            const strVal = `<em style='color:grey'>Val: ${computedValue}</em>`
            ele.innerHTML = strVal;
            const newEle = document.createElement("div");
            newEle.innerHTML = `<b>${computedValue2}</b>`;
            ele.appendChild(newEle);
            await timeout(0);
        }

        const end = new Date();
        compData.runningdomTest = false;

        return end.valueOf() - start.valueOf();
    }

    async function remoteDownloadTest(): Promise<void>
    {
        compData.runningRemoteDownloadTest = true;

        const thirdPartyDownloadResults = await timeEndpointDownload(compData.settings.thirdPartyDownloadUrl);
        compData.results.downloadThirdPartyTime = thirdPartyDownloadResults?.timeInMilliSeconds;
        compData.results.downloadThirdPartyFailureCount = thirdPartyDownloadResults?.failureCount;

        const ourServerDownloadResults = await timeEndpointDownload(ChoiceApi.customerSupport.byteArrayUrl(compData.settings.downloadResourceByteCount));
        compData.results.downloadOurServerTime = ourServerDownloadResults?.timeInMilliSeconds;
        compData.results.downloadOurServerFailureCount = ourServerDownloadResults?.failureCount;
        
        compData.runningRemoteDownloadTest = false;
    }

    interface DownloadResults
    {
        timeInMilliSeconds?: number,
        failureCount?: number
    };

    async function timeEndpointDownload(endpoint: string): Promise<DownloadResults|undefined>
    {
        const result: DownloadResults = {};

        const repTimes: (number|undefined)[] = [];
        while((repTimes.length < compData.settings.downloadResourceReps))
        {
            // Try 10 times to download before we give up
            if ((result.failureCount ?? 0) >= 10)
                break;

            const endpointUrlWithSalt = `${endpoint}?${Date.now()}`;
            const time = await timeDownload(endpointUrlWithSalt);

            // If a download fails, increment the failure count
            if (undefined === time)
            {
                result.failureCount = (result.failureCount ?? 0) + 1;
                await timeout(1000);    // Wait a bit before retrying
                continue;
            }
                
            repTimes.push(time);
        }

        // Determine the total download time 
        if (repTimes.length > 1)
        {
            // Clear the first download in case the server has just re-started.
            //  Don't want cold starts times to influence results
            repTimes[0] = 0;

            result.timeInMilliSeconds = _.sum(repTimes);
        }

        return result;
    }

    async function timeDownload(endpoint: string): Promise<number|undefined>
    {        
        try
        {
            const startTime = new Date();
            const result = await fetch(endpoint);
            const endTime = new Date();
    
            if (200 != result.status)
                return;
            return endTime.valueOf() - startTime.valueOf();;
        }
        catch(_error)
        {
            return;
        }
    }

    async function runTests(): Promise<void>
    {
        try
        {
            await nextTick();
        
            while (true)
            {
                // Make sure the document is visible before starting tests
                while ("visible" !== document.visibilityState)
                {
                    // Wait a moment to check if the document is visible
                    await timeout(1000);
                }
                compData.restartTests = false;
                compData.results = {};

                compData.results.domTestTime = await domTest();
                if (compData.restartTests)
                    continue;

                compData.results.fibonacciTestTime = await fibonacciTest(100);
                if (compData.restartTests)
                    continue;
    
                await remoteDownloadTest();
                if (compData.restartTests)
                    continue;

                if (!compData.restartTests)
                    break;
            }
        }
        catch(errTest)
        {
            const errMsg = errTest instanceof Error ? errTest.message : "Unknown error";
            alert(`Error running tests: ${errMsg}`);
        }
    }

    function onVisiblityChanged(isVisible: boolean)
    {
        if (compData.runningTests && !isVisible)
        {
            compData.restartTests = true;
        }
    }
</script>

<template>
    <VisibilityMonitor @visibile="onVisiblityChanged"/>

    <div>
        <div style="margin-left:1rem">  
            <span v-if="compData.runningTests" style="color:dodgerblue;">
                Running Tests...
            </span>
    
        
            <div v-if="false === compData.runningTests">
                <div>Fibonacci: {{ compData.results.fibonacciTestTime }} ms</div>
                <div>Dom: {{ compData.results.domTestTime }} ms</div>
                <div>Remote Download: {{ compData.results.downloadThirdPartyTime }} ms</div>
                <div>OfficeMd Download: {{ compData.results.downloadOurServerTime }} ms</div>
            </div>
    
            <div id="domTestArea" v-show="compData.runningdomTest">
                domTestArea...
            </div>        
    
            <div id="fibonacciTestArea" v-show="compData.runningfibonacciTest">
                Fibonacci Status: {{ compData.fibonacciTestStatus }}
            </div>        
    
    
            <div v-if="compData.runningRemoteDownloadTest">
                Testing Remote Download
            </div>
        </div>
    </div>
</template>